import React, { useState, useRef } from 'react';
import { useNavigate } from 'react-router-dom';
import axios from 'axios';
import {
  ArrowLeft, ArrowRight, Plus, Trash2, Sparkles, Upload,
  Check, X, Heart, Zap, Users, Globe, ShieldCheck, TrendingUp, ChevronDown
} from 'lucide-react';

const BACKEND_URL = (typeof window !== 'undefined' && window.location.origin) || process.env.REACT_APP_BACKEND_URL;
const MAX_PRODUCTS = 10;
const MAX_IMAGE_BYTES = 3 * 1024 * 1024; // 3 MB per product image

const emptyProduct = () => ({
  name: '',
  description: '',
  price: '',
  image_data_url: '',
  image_name: '',
  product_url: ''
});

const readFileAsDataUrl = (file) =>
  new Promise((resolve, reject) => {
    const reader = new FileReader();
    reader.onload = () => resolve(reader.result);
    reader.onerror = () => reject(reader.error);
    reader.readAsDataURL(file);
  });

const ShowcaseBrand = () => {
  const navigate = useNavigate();
  const formRef = useRef(null);
  const [openFaq, setOpenFaq] = useState(null);
  const scrollToForm = () => {
    formRef.current?.scrollIntoView({ behavior: 'smooth', block: 'start' });
  };
  const [form, setForm] = useState({
    brand_name: '',
    founder_name: '',
    email: '',
    phone: '',
    city: '',
    instagram_handle: '',
    website_url: '',
    brand_description: '',
    wants_managed_service: false,
    message: ''
  });
  const [products, setProducts] = useState([emptyProduct()]);
  const [submitting, setSubmitting] = useState(false);

  const handleBrandChange = (e) => {
    const { name, value, type, checked } = e.target;
    setForm((prev) => ({ ...prev, [name]: type === 'checkbox' ? checked : value }));
  };

  const handleProductChange = (idx, field, value) => {
    setProducts((prev) => {
      const next = [...prev];
      next[idx] = { ...next[idx], [field]: value };
      return next;
    });
  };

  const handleProductImage = async (idx, file) => {
    if (!file) return;
    if (file.size > MAX_IMAGE_BYTES) {
      alert(`Image too large. Please keep under ${MAX_IMAGE_BYTES / (1024 * 1024)} MB.`);
      return;
    }
    try {
      const dataUrl = await readFileAsDataUrl(file);
      handleProductChange(idx, 'image_data_url', dataUrl);
      handleProductChange(idx, 'image_name', file.name);
    } catch (err) {
      alert('Could not read the image file. Please try another.');
    }
  };

  const addProduct = () => {
    if (products.length >= MAX_PRODUCTS) {
      alert(`Maximum ${MAX_PRODUCTS} products per submission.`);
      return;
    }
    setProducts((prev) => [...prev, emptyProduct()]);
  };

  const removeProduct = (idx) => {
    setProducts((prev) => (prev.length === 1 ? prev : prev.filter((_, i) => i !== idx)));
  };

  const handleSubmit = async (e) => {
    e.preventDefault();

    for (let i = 0; i < products.length; i += 1) {
      const p = products[i];
      if (!p.name.trim() || !p.price || !p.image_data_url) {
        alert(`Please complete product #${i + 1}: name, price, and image are required.`);
        return;
      }
      if (Number(p.price) <= 0) {
        alert(`Product #${i + 1} price must be greater than 0.`);
        return;
      }
    }

    try {
      setSubmitting(true);
      const payload = {
        ...form,
        products: products.map((p) => ({
          name: p.name.trim(),
          description: p.description.trim(),
          price: Number(p.price),
          image_data_url: p.image_data_url,
          product_url: p.product_url.trim() || null
        }))
      };

      await axios.post(`${BACKEND_URL}/api/brand-submissions`, payload);

      const msg = form.wants_managed_service
        ? `Thank you! Our e-commerce team will reach out within 48 hours about the ₹2,323 / month managed service (website + Instagram landing + SMM).`
        : `Thank you! Your brand submission is under review. Approved brands will be showcased under Featured Brands.`;
      alert(msg);
      navigate('/');
    } catch (err) {
      const detail = err.response?.data?.detail || 'Submission failed. Please try again.';
      alert(detail);
    } finally {
      setSubmitting(false);
    }
  };

  const faqs = [
    {
      q: 'How much does it cost to be listed on Nanaki Kaur?',
      a: 'Listing under Featured Brands is completely free. You only pay if you opt into the ₹2,323/month full digital presence service (website + Instagram landing + SMM). No upfront fees, no hidden charges, no commission cuts on sales made on your own site.'
    },
    {
      q: 'Where do my products link to?',
      a: 'Each product card on Nanaki Kaur deep-links to a URL you control — your own website, Shopify store, Instagram product link, or your brand’s landing page. We send shoppers straight to you; we don’t intercept the sale.'
    },
    {
      q: 'Who reviews my application?',
      a: 'Our in-house team manually reviews every brand within ~48 hours. We look for women-led ownership, original products, ethical practices and a clear brand voice. We’ll email you with the decision.'
    },
    {
      q: 'What does the ₹2,323/month plan actually deliver?',
      a: 'A clean static website hosted by us (your own domain optional), a curated Instagram landing page with bio-link setup, monthly Social Media Marketing campaigns by our team, and ongoing site maintenance. You can cancel anytime.'
    },
    {
      q: 'Do I need a website to apply?',
      a: 'Not at all — an Instagram handle is enough to start. If you don’t have either, the ₹2,323/month plan covers building one for you.'
    },
    {
      q: 'Can I update my listed products later?',
      a: 'Yes. Email jasjeevbhasin5757@gmail.com with the changes and we’ll update your catalogue within 24 hours.'
    }
  ];

  return (
    <div className="min-h-screen bg-white" data-testid="showcase-brand-page">
      {/* Back link */}
      <div className="max-w-6xl mx-auto px-4 sm:px-6 lg:px-8 pt-6">
        <button
          onClick={() => navigate('/')}
          className="flex items-center text-purple-600 hover:text-purple-700 transition"
          data-testid="back-to-home-btn"
        >
          <ArrowLeft className="w-4 h-4 mr-2" />
          Back to Home
        </button>
      </div>

      {/* HERO */}
      <section className="relative overflow-hidden bg-gradient-to-br from-pink-600 via-purple-600 to-indigo-600 text-white">
        <div className="absolute inset-0 opacity-20" style={{
          backgroundImage: 'radial-gradient(circle at 20% 20%, rgba(255,255,255,0.4) 0, transparent 40%), radial-gradient(circle at 80% 60%, rgba(255,255,255,0.3) 0, transparent 40%)'
        }} />
        <div className="relative max-w-5xl mx-auto px-4 sm:px-6 lg:px-8 py-16 md:py-24 text-center">
          <span className="inline-block bg-white/20 backdrop-blur-sm rounded-full px-4 py-1 text-xs font-semibold uppercase tracking-wider mb-6">
            For budding female entrepreneurs
          </span>
          <h1 className="text-4xl sm:text-5xl lg:text-6xl font-bold mb-6 leading-tight">
            Get your brand seen by<br className="hidden sm:block" />{' '}a community that actually cares.
          </h1>
          <p className="text-lg md:text-xl opacity-95 max-w-3xl mx-auto mb-10">
            Nanaki Kaur is a curated marketplace celebrating women-led brands.
            List your products with us for free — or let our team build & promote your full digital presence for ₹2,323/month.
          </p>
          <div className="flex flex-col sm:flex-row gap-4 justify-center">
            <button
              type="button"
              onClick={scrollToForm}
              className="bg-white text-pink-700 px-8 py-3 rounded-lg font-semibold hover:bg-pink-50 transition flex items-center justify-center"
              data-testid="hero-apply-btn"
            >
              Apply for free showcase
              <ArrowRight className="w-5 h-5 ml-2" />
            </button>
            <a
              href="#plans"
              className="bg-white/10 border border-white/40 backdrop-blur text-white px-8 py-3 rounded-lg font-semibold hover:bg-white/20 transition flex items-center justify-center"
            >
              Compare plans
            </a>
          </div>
          <p className="text-xs opacity-75 mt-6">
            ✨ No upfront cost · No long-term lock-in · Curated by Nanaki Kaur
          </p>
        </div>
      </section>

      {/* PLANS COMPARISON */}
      <section id="plans" className="py-16 md:py-20 bg-gray-50">
        <div className="max-w-6xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="text-center mb-12">
            <h2 className="text-3xl md:text-4xl font-bold mb-3">Two ways to grow with us</h2>
            <p className="text-gray-600 max-w-2xl mx-auto">
              Start free, or unlock end-to-end digital growth — pick what fits where your brand is today.
            </p>
          </div>
          <div className="grid md:grid-cols-2 gap-6 lg:gap-8">
            {/* Free tier */}
            <div className="bg-white rounded-2xl p-8 shadow-sm border-2 border-gray-100 hover:border-pink-200 transition flex flex-col">
              <div className="mb-2 text-xs font-semibold uppercase tracking-wider text-gray-500">Most popular</div>
              <h3 className="text-2xl font-bold mb-2">Free Showcase</h3>
              <p className="text-4xl font-bold mb-1">₹0</p>
              <p className="text-sm text-gray-500 mb-6">forever, no strings attached</p>
              <ul className="space-y-3 mb-8 flex-1">
                <li className="flex items-start"><Check className="w-5 h-5 text-green-600 flex-shrink-0 mt-0.5 mr-3" /><span>Up to <strong>10 products</strong> listed under Featured Brands</span></li>
                <li className="flex items-start"><Check className="w-5 h-5 text-green-600 flex-shrink-0 mt-0.5 mr-3" /><span>Each product card deep-links to <strong>your own site</strong> or Instagram</span></li>
                <li className="flex items-start"><Check className="w-5 h-5 text-green-600 flex-shrink-0 mt-0.5 mr-3" /><span>Brand credit prominently shown on every product</span></li>
                <li className="flex items-start"><Check className="w-5 h-5 text-green-600 flex-shrink-0 mt-0.5 mr-3" /><span>Discoverability through Nanaki&apos;s Kaurs referral network</span></li>
                <li className="flex items-start"><X className="w-5 h-5 text-gray-300 flex-shrink-0 mt-0.5 mr-3" /><span className="text-gray-400">Custom website / Instagram landing page</span></li>
                <li className="flex items-start"><X className="w-5 h-5 text-gray-300 flex-shrink-0 mt-0.5 mr-3" /><span className="text-gray-400">Managed social media marketing</span></li>
              </ul>
              <button
                type="button"
                onClick={scrollToForm}
                className="w-full bg-gray-900 text-white py-3 rounded-lg font-semibold hover:bg-gray-800 transition"
                data-testid="plan-free-cta"
              >
                Apply for free showcase
              </button>
            </div>
            {/* Paid tier */}
            <div className="bg-gradient-to-br from-pink-50 via-white to-purple-50 rounded-2xl p-8 shadow-md border-2 border-pink-300 relative flex flex-col">
              <span className="absolute -top-3 right-6 bg-pink-600 text-white text-xs font-bold px-3 py-1 rounded-full uppercase tracking-wide">Done for you</span>
              <div className="mb-2 text-xs font-semibold uppercase tracking-wider text-pink-600">Recommended for new brands</div>
              <h3 className="text-2xl font-bold mb-2">Full Digital Presence</h3>
              <p className="text-4xl font-bold mb-1">₹2,323<span className="text-lg font-normal text-gray-500">/month</span></p>
              <p className="text-sm text-gray-500 mb-6">flat fee · cancel anytime</p>
              <ul className="space-y-3 mb-8 flex-1">
                <li className="flex items-start"><Check className="w-5 h-5 text-pink-600 flex-shrink-0 mt-0.5 mr-3" /><span>Everything in <strong>Free Showcase</strong>, plus…</span></li>
                <li className="flex items-start"><Check className="w-5 h-5 text-pink-600 flex-shrink-0 mt-0.5 mr-3" /><span>Static <strong>brand website</strong> designed &amp; hosted by us</span></li>
                <li className="flex items-start"><Check className="w-5 h-5 text-pink-600 flex-shrink-0 mt-0.5 mr-3" /><span>Custom <strong>Instagram landing page</strong> + bio-link setup</span></li>
                <li className="flex items-start"><Check className="w-5 h-5 text-pink-600 flex-shrink-0 mt-0.5 mr-3" /><span>Monthly <strong>Social Media Marketing</strong> campaigns</span></li>
                <li className="flex items-start"><Check className="w-5 h-5 text-pink-600 flex-shrink-0 mt-0.5 mr-3" /><span>Ongoing site maintenance &amp; content updates</span></li>
                <li className="flex items-start"><Check className="w-5 h-5 text-pink-600 flex-shrink-0 mt-0.5 mr-3" /><span>Priority placement across the platform</span></li>
              </ul>
              <button
                type="button"
                onClick={scrollToForm}
                className="w-full bg-gradient-to-r from-pink-600 to-purple-600 text-white py-3 rounded-lg font-semibold hover:opacity-90 transition"
                data-testid="plan-paid-cta"
              >
                Get full digital presence
              </button>
            </div>
          </div>
        </div>
      </section>

      {/* HOW IT WORKS */}
      <section className="py-16 md:py-20 bg-white">
        <div className="max-w-5xl mx-auto px-4 sm:px-6 lg:px-8">
          <h2 className="text-3xl md:text-4xl font-bold text-center mb-3">How it works</h2>
          <p className="text-gray-600 text-center mb-12 max-w-2xl mx-auto">
            From application to live storefront in under a week.
          </p>
          <div className="grid md:grid-cols-4 gap-8">
            {[
              { n: 1, t: 'Apply', d: 'Tell us about your brand and add up to 10 products with photos & prices.', i: <Sparkles className="w-6 h-6" /> },
              { n: 2, t: 'Review', d: 'Our team reviews your application within 48 hours to make sure it fits our community.', i: <ShieldCheck className="w-6 h-6" /> },
              { n: 3, t: 'Go live', d: 'Approved brands appear under Featured Brands with direct links to your store.', i: <Globe className="w-6 h-6" /> },
              { n: 4, t: 'Grow', d: 'Optionally upgrade to the ₹2,323/mo plan for a managed website + SMM.', i: <TrendingUp className="w-6 h-6" /> }
            ].map((s) => (
              <div key={s.n}>
                <div className="w-12 h-12 rounded-full bg-pink-100 text-pink-700 flex items-center justify-center mb-4">{s.i}</div>
                <span className="text-xs font-bold uppercase tracking-wider text-pink-600">Step {s.n}</span>
                <h3 className="font-bold text-lg mt-1 mb-2">{s.t}</h3>
                <p className="text-gray-600 text-sm leading-relaxed">{s.d}</p>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* WHO IS THIS FOR */}
      <section className="py-16 md:py-20 bg-gradient-to-br from-pink-50 to-purple-50">
        <div className="max-w-5xl mx-auto px-4 sm:px-6 lg:px-8">
          <h2 className="text-3xl md:text-4xl font-bold text-center mb-3">Who is this for?</h2>
          <p className="text-gray-600 text-center mb-12 max-w-2xl mx-auto">
            If you&apos;re nodding along to any of these, you belong here.
          </p>
          <div className="grid md:grid-cols-3 gap-6">
            {[
              { i: <Heart className="w-5 h-5" />, t: 'Women-founded brands', d: 'Independent labels, makers and entrepreneurs led by women.' },
              { i: <Zap className="w-5 h-5" />, t: 'Just starting out', d: 'You have a few products ready and need eyes on them — fast.' },
              { i: <Users className="w-5 h-5" />, t: 'Community-minded', d: 'You believe in lifting other women as you climb.' }
            ].map((it) => (
              <div key={it.t} className="bg-white rounded-xl p-6 shadow-sm">
                <div className="w-10 h-10 rounded-lg bg-pink-100 text-pink-600 flex items-center justify-center mb-4">{it.i}</div>
                <h3 className="font-bold mb-2">{it.t}</h3>
                <p className="text-sm text-gray-600">{it.d}</p>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* FAQ */}
      <section className="py-16 md:py-20 bg-white">
        <div className="max-w-3xl mx-auto px-4 sm:px-6 lg:px-8">
          <h2 className="text-3xl md:text-4xl font-bold text-center mb-10">Quick questions</h2>
          <div className="space-y-3">
            {faqs.map((f, i) => (
              <div key={i} className="border border-gray-200 rounded-lg overflow-hidden bg-white" data-testid={`faq-item-${i}`}>
                <button
                  type="button"
                  onClick={() => setOpenFaq(openFaq === i ? null : i)}
                  className="w-full flex items-center justify-between px-5 py-4 text-left hover:bg-gray-50 transition"
                >
                  <span className="font-semibold pr-4">{f.q}</span>
                  <ChevronDown className={`w-5 h-5 flex-shrink-0 transition-transform ${openFaq === i ? 'rotate-180' : ''}`} />
                </button>
                {openFaq === i && (
                  <div className="px-5 pb-4 text-gray-600 text-sm leading-relaxed">{f.a}</div>
                )}
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* APPLICATION FORM */}
      <section ref={formRef} id="apply" className="py-16 md:py-20 bg-gray-50 scroll-mt-20">
        <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="text-center mb-10">
            <h2 className="text-3xl md:text-4xl font-bold mb-3">Apply now</h2>
            <p className="text-gray-600 max-w-2xl mx-auto">
              Takes about 5 minutes. We&apos;ll review and email you within 48 hours.
            </p>
          </div>
        {/* Form */}
        <form onSubmit={handleSubmit} className="bg-white rounded-lg shadow-md p-8 space-y-8" data-testid="showcase-brand-form">
          {/* Brand basics */}
          <div>
            <h2 className="text-2xl font-bold mb-6">Tell us about your brand</h2>
            <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-2">
                  Brand Name <span className="text-red-500">*</span>
                </label>
                <input
                  type="text"
                  name="brand_name"
                  value={form.brand_name}
                  onChange={handleBrandChange}
                  required
                  className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-pink-600 focus:border-transparent"
                  data-testid="brand-name-input"
                />
              </div>
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-2">
                  Founder / Owner Name <span className="text-red-500">*</span>
                </label>
                <input
                  type="text"
                  name="founder_name"
                  value={form.founder_name}
                  onChange={handleBrandChange}
                  required
                  className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-pink-600 focus:border-transparent"
                  data-testid="founder-name-input"
                />
              </div>
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-2">
                  Email <span className="text-red-500">*</span>
                </label>
                <input
                  type="email"
                  name="email"
                  value={form.email}
                  onChange={handleBrandChange}
                  required
                  className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-pink-600 focus:border-transparent"
                  data-testid="email-input"
                />
              </div>
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-2">
                  Phone <span className="text-red-500">*</span>
                </label>
                <input
                  type="tel"
                  name="phone"
                  value={form.phone}
                  onChange={handleBrandChange}
                  required
                  placeholder="+91 XXXXX XXXXX"
                  className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-pink-600 focus:border-transparent"
                  data-testid="phone-input"
                />
              </div>
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-2">City</label>
                <input
                  type="text"
                  name="city"
                  value={form.city}
                  onChange={handleBrandChange}
                  className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-pink-600 focus:border-transparent"
                />
              </div>
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-2">Instagram Handle</label>
                <input
                  type="text"
                  name="instagram_handle"
                  value={form.instagram_handle}
                  onChange={handleBrandChange}
                  placeholder="@yourbrand"
                  className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-pink-600 focus:border-transparent"
                />
              </div>
              <div className="md:col-span-2">
                <label className="block text-sm font-medium text-gray-700 mb-2">Existing Website (if any)</label>
                <input
                  type="url"
                  name="website_url"
                  value={form.website_url}
                  onChange={handleBrandChange}
                  placeholder="https://yourbrand.com"
                  className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-pink-600 focus:border-transparent"
                  data-testid="website-url-input"
                />
              </div>
            </div>

            <div className="mt-6">
              <label className="block text-sm font-medium text-gray-700 mb-2">
                Brand Story / Description <span className="text-red-500">*</span>
              </label>
              <textarea
                name="brand_description"
                value={form.brand_description}
                onChange={handleBrandChange}
                required
                rows={4}
                placeholder="What makes your brand special? Who are you creating for?"
                className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-pink-600 focus:border-transparent"
                data-testid="brand-description-input"
              />
            </div>
          </div>

          {/* Products */}
          <div>
            <div className="flex items-center justify-between mb-4">
              <h2 className="text-2xl font-bold">Your products ({products.length}/{MAX_PRODUCTS})</h2>
              <button
                type="button"
                onClick={addProduct}
                className="flex items-center text-sm px-3 py-2 bg-pink-600 text-white rounded-lg hover:bg-pink-700 transition"
                data-testid="add-product-btn"
              >
                <Plus className="w-4 h-4 mr-1" />
                Add product
              </button>
            </div>

            <div className="space-y-6">
              {products.map((product, idx) => (
                <div
                  key={idx}
                  className="border border-gray-200 rounded-lg p-4 bg-gray-50"
                  data-testid={`product-row-${idx}`}
                >
                  <div className="flex items-center justify-between mb-4">
                    <h3 className="font-semibold text-gray-800">Product #{idx + 1}</h3>
                    {products.length > 1 && (
                      <button
                        type="button"
                        onClick={() => removeProduct(idx)}
                        className="text-red-600 hover:text-red-800 text-sm flex items-center"
                        data-testid={`remove-product-${idx}`}
                      >
                        <Trash2 className="w-4 h-4 mr-1" />
                        Remove
                      </button>
                    )}
                  </div>

                  <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                    <div>
                      <label className="block text-sm font-medium text-gray-700 mb-1">
                        Product Name <span className="text-red-500">*</span>
                      </label>
                      <input
                        type="text"
                        value={product.name}
                        onChange={(e) => handleProductChange(idx, 'name', e.target.value)}
                        required
                        className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-pink-600 focus:border-transparent"
                        data-testid={`product-name-${idx}`}
                      />
                    </div>
                    <div>
                      <label className="block text-sm font-medium text-gray-700 mb-1">
                        Price (INR) <span className="text-red-500">*</span>
                      </label>
                      <input
                        type="number"
                        min="1"
                        step="1"
                        value={product.price}
                        onChange={(e) => handleProductChange(idx, 'price', e.target.value)}
                        required
                        className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-pink-600 focus:border-transparent"
                        data-testid={`product-price-${idx}`}
                      />
                    </div>
                    <div className="md:col-span-2">
                      <label className="block text-sm font-medium text-gray-700 mb-1">Short Description</label>
                      <textarea
                        rows={2}
                        value={product.description}
                        onChange={(e) => handleProductChange(idx, 'description', e.target.value)}
                        className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-pink-600 focus:border-transparent"
                      />
                    </div>
                    <div className="md:col-span-2">
                      <label className="block text-sm font-medium text-gray-700 mb-1">
                        Product link on your site (optional)
                      </label>
                      <input
                        type="url"
                        value={product.product_url}
                        onChange={(e) => handleProductChange(idx, 'product_url', e.target.value)}
                        placeholder="https://yourbrand.com/product/..."
                        className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-pink-600 focus:border-transparent"
                      />
                    </div>
                    <div className="md:col-span-2">
                      <label className="block text-sm font-medium text-gray-700 mb-1">
                        Product Photo <span className="text-red-500">*</span>
                      </label>
                      <div className="flex items-center space-x-4">
                        <label
                          className="flex items-center px-4 py-2 bg-white border border-dashed border-pink-400 text-pink-700 rounded-lg cursor-pointer hover:bg-pink-50 transition text-sm"
                          data-testid={`product-image-label-${idx}`}
                        >
                          <Upload className="w-4 h-4 mr-2" />
                          {product.image_name ? 'Replace photo' : 'Upload photo'}
                          <input
                            type="file"
                            accept="image/*"
                            className="hidden"
                            onChange={(e) => handleProductImage(idx, e.target.files?.[0])}
                          />
                        </label>
                        {product.image_name && (
                          <span className="text-xs text-gray-600 truncate max-w-[10rem]">{product.image_name}</span>
                        )}
                      </div>
                      {product.image_data_url && (
                        <img
                          src={product.image_data_url}
                          alt="preview"
                          className="mt-3 w-32 h-32 object-cover rounded-lg border border-gray-200"
                        />
                      )}
                    </div>
                  </div>
                </div>
              ))}
            </div>
          </div>

          {/* Managed service */}
          <div className="bg-pink-50 border border-pink-200 rounded-lg p-4">
            <label className="flex items-start space-x-3 cursor-pointer">
              <input
                type="checkbox"
                name="wants_managed_service"
                checked={form.wants_managed_service}
                onChange={handleBrandChange}
                className="mt-1 w-5 h-5 text-pink-600 border-gray-300 rounded focus:ring-pink-600"
                data-testid="managed-service-checkbox"
              />
              <span className="text-sm text-gray-800">
                Yes, I&apos;d like Nanaki Kaur&apos;s team to build and promote my static website + Instagram
                landing page for <strong>₹2,323 / month</strong> (maintenance + social media marketing included).
              </span>
            </label>
          </div>

          <div>
            <label className="block text-sm font-medium text-gray-700 mb-2">Anything else we should know?</label>
            <textarea
              name="message"
              value={form.message}
              onChange={handleBrandChange}
              rows={3}
              className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-pink-600 focus:border-transparent"
            />
          </div>

          <button
            type="submit"
            disabled={submitting}
            className="w-full bg-gradient-to-r from-pink-600 to-purple-600 text-white font-semibold py-3 rounded-lg hover:opacity-90 transition disabled:opacity-60 disabled:cursor-not-allowed"
            data-testid="submit-brand-btn"
          >
            {submitting ? 'Submitting...' : 'Submit my brand for review'}
          </button>

          <p className="text-xs text-center text-gray-500">
            Approved submissions appear in the <strong>Featured Brands</strong> category with a direct link back to
            your website.
          </p>
        </form>
        </div>
      </section>
    </div>
  );
};

export default ShowcaseBrand;
